import {
  MongoCustomerId,
  MongoTeamId,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import {
  supportTicketFilterData,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import CustomerIcon from "@redotech/redo-icons/user-01.svg";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import type { ArrayStringTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { Effect } from "effect";

const CUSTOMER_SEARCH_LIMIT = 25;

/** Names for customer ids the picker has seen; the chip stores ids, shows names. */
export const customerNameCache = new Map<string, string>();

/** Values are prefixed `MongoCustomerId`s, matching the index's `mongoCustomerId`. */
export function createCustomerAdvancedFilter(
  effectApi?: EffectApiContext,
  teamId?: string,
): ArrayStringTableFilter<string> {
  return {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: CustomerIcon,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    data: supportTicketFilterData[SupportTicketFilterNames.CUSTOMER],
    itemLabel: "customer",
    itemLabelPlural: "customers",
    displayName: "Customer",
    // Per-keystroke server search; a team's customer book is too large to list.
    dbSearch: true,
    valueFetcher:
      effectApi && teamId
        ? async ({ filterSearch }, _signal) => {
            const client = effectApi.getClient();
            // Empty search is meaningful: it returns recent customers.
            const result = await effectApi.runPromise(
              client.supportTicketRelatedRecords
                .searchSupportCustomers({
                  urlParams: {
                    teamId: MongoTeamId.fromString(teamId),
                    searchText: filterSearch?.trim() ?? "",
                    limit: CUSTOMER_SEARCH_LIMIT,
                  },
                })
                .pipe(Effect.orDie),
            );
            return result.results.map((customer) => {
              const id = MongoCustomerId.fromString(customer.id) as string;
              customerNameCache.set(
                id,
                customer.email
                  ? `${customer.customerName} (${customer.email})`
                  : customer.customerName,
              );
              return id;
            });
          }
        : undefined,
    display: (item: string) => customerNameCache.get(item) ?? item,
  };
}
